import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { dotoniwoofApi } from '../api/dotoniwooApi';
import { Col, Container, Row, Card, Form, Spinner, Alert } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useBranding } from '../context/BrandingContext';
import './ModernDashboard.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const GroomerDashboard = () => {
    const [groomingData, setGroomingData] = useState({
        labels: [],
        datasets: [],
    });

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fechaActual = new Date();
    const year = fechaActual.getFullYear().toString();
    const [SelectedYear, setSelectedYear] = useState(year);

    // Configuración desde branding
    const { colors, companyName } = useBranding();
    const brandColors = {
        primary: colors.secondary,
        secondary: colors.accent,
        success: colors.success,
        warning: colors.warning,
        danger: colors.danger,
        info: colors.info
    };

    const hexToRgba = (hex, alpha) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    const chartColors = {
        sales: {
            background: hexToRgba(colors.secondary, 0.1),
            border: colors.secondary,
            hover: hexToRgba(colors.secondary, 0.2)
        },
        commissions: {
            background: hexToRgba(colors.success, 0.1),
            border: colors.success,
            hover: hexToRgba(colors.success, 0.2)
        }
    };

    // Cargar datos de grooming por mes
    useEffect(() => {
        const fetchGroomingData = async () => {
            try {
                setLoading(true);
                setError(null);

                console.log('🔄 Fetching grooming sales data for year:', SelectedYear);
                
                const response = await dotoniwoofApi.get(`/sale.php/sale-gromming-month?year=${SelectedYear}`);
                
                console.log('✅ Grooming response:', response.data);
                
                if (response.data && response.data.status === 'ok' && response.data.data) {
                    const { months, sales, commissions } = response.data.data;
                    
                    // Traducir meses al español
                    const monthTranslations = {
                        'January': 'Enero',
                        'February': 'Febrero',
                        'March': 'Marzo',
                        'April': 'Abril',
                        'May': 'Mayo',
                        'June': 'Junio',
                        'July': 'Julio',
                        'August': 'Agosto',
                        'September': 'Septiembre',
                        'October': 'Octubre',
                        'November': 'Noviembre',
                        'December': 'Diciembre'
                    };
                    
                    const translatedMonths = months.map(month => monthTranslations[month] || month);
                    
                    setGroomingData({
                        labels: translatedMonths,
                        datasets: [
                            {
                                label: 'Ventas ($)',
                                data: sales,
                                backgroundColor: chartColors.sales.background,
                                borderColor: chartColors.sales.border,
                                borderWidth: 2,
                                borderRadius: 6,
                                borderSkipped: false,
                            },
                            {
                                label: 'Comisiones ($)',
                                data: commissions,
                                backgroundColor: chartColors.commissions.background,
                                borderColor: chartColors.commissions.border,
                                borderWidth: 2,
                                borderRadius: 6,
                                borderSkipped: false,
                            },
                        ],
                    });
                } else {
                    setError('Error al cargar datos de grooming');
                }
            } catch (err) {
                setError('Error al cargar datos de grooming');
                console.error('Error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchGroomingData();
    }, [SelectedYear]);

    // Configuración de opciones para gráficos
    const getChartOptions = (title) => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    usePointStyle: true,
                    padding: 20,
                    font: {
                        size: 12,
                        weight: '500'
                    }
                }
            },
            title: {
                display: true,
                text: title,
                font: {
                    size: 16,
                    weight: 'bold'
                },
                color: brandColors.secondary,
                padding: {
                    top: 10,
                    bottom: 20
                }
            },
            tooltip: {
                backgroundColor: 'rgba(51, 46, 90, 0.9)',
                titleColor: '#fff',
                bodyColor: '#fff',
                borderColor: brandColors.primary,
                borderWidth: 1,
                cornerRadius: 8,
                displayColors: true,
                padding: 12
            }
        },
        scales: {
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    font: {
                        size: 11
                    },
                    color: brandColors.secondary
                }
            },
            y: {
                grid: {
                    color: 'rgba(51, 46, 90, 0.1)',
                    drawBorder: false
                },
                ticks: {
                    font: {
                        size: 11
                    },
                    color: brandColors.secondary,
                    callback: function(value) {
                        return new Intl.NumberFormat('es-CO', {
                            style: 'currency',
                            currency: 'COP',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                        }).format(value);
                    }
                }
            }
        },
        interaction: {
            intersect: false,
            mode: 'index'
        }
    });

    if (loading) {
        return (
            <div className="dashboard-loading">
                <Spinner animation="border" variant="primary" />
                <p>Cargando dashboard...</p>
            </div>
        );
    }

    if (error) {
        return (
            <Container className="mt-4">
                <Alert variant="danger">
                    <Alert.Heading>Error al cargar el dashboard</Alert.Heading>
                    <p>{error}</p>
                </Alert>
            </Container>
        );
    }

    return (
        <div className="modern-dashboard">
            <Container fluid className="dashboard-container">
                {/* Header del Dashboard */}
                <motion.div 
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="dashboard-header"
                >
                    <h1 className="dashboard-title">
                        <i className="fas fa-cut me-3"></i>
                        Dashboard Groomer - {companyName}
                    </h1>
                    <p className="dashboard-subtitle">
                        Análisis de ventas y comisiones de grooming
                    </p>
                </motion.div>

                {/* Controles de Filtros */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="dashboard-controls"
                >
                    <Row className="g-3">
                        <Col md={12}>
                            <Card className="filter-card">
                                <Card.Body>
                                    <Form.Group>
                                        <Form.Label className="filter-label">
                                            <i className="fas fa-calendar-alt me-2"></i>
                                            Año
                                        </Form.Label>
                                        <Form.Select 
                                            value={SelectedYear} 
                                            onChange={(e) => setSelectedYear(e.target.value)}
                                            className="modern-select"
                                        >
                                            <option value={year}>Año actual ({year})</option>
                                            <option value="2025">2025</option>
                                            <option value="2024">2024</option>
                                            <option value="2023">2023</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </motion.div>

                {/* Gráfico de Ventas y Comisiones de Grooming */}
                <Row className="g-4 mt-4">
                    <Col xl={12}>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                        >
                            <Card className="chart-card">
                                <Card.Header className="chart-header">
                                    <h5 className="chart-title">
                                        <i className="fas fa-cut me-2"></i>
                                        Ventas y Comisiones de Grooming por Mes
                                    </h5>
                                </Card.Header>
                                <Card.Body className="chart-body">
                                    <div className="chart-container-large">
                                        <Bar 
                                            data={groomingData} 
                                            options={getChartOptions('Ventas y Comisiones de Grooming por Mes')} 
                                        />
                                    </div>
                                </Card.Body>
                            </Card>
                        </motion.div>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default GroomerDashboard;

